/**
 * LoginButton — the one sign-in window (web2 migration, step 1). Same dialog as before, but
 * every entry is Supabase Auth: Email (a one-time code, or the magic link in the same email)
 * and OAuth (Google + the social row). No wallets: the wallet-era dialog is parked, not
 * deleted, in `web3/Web3LoginButton.tsx`.
 *
 * Supabase proves who the player is; AccountProvider exchanges that for the account session
 * the rest of the app already uses, so nothing downstream changes.
 */
import { useContext, useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Dialog, Flex, Text, Button, TextField } from '@radix-ui/themes';
import { Mail, Gamepad2 } from 'lucide-react';
import { AccountContext, useAccountId, type OAuthProvider } from '../../auth/useAccountId';
import { useEnabledOAuthProviders } from '../../auth/useEnabledOAuthProviders';
import { getGuestFreePetId } from '../../auth/guestPlay';
import { getPortal } from '../../lib/portalEmbed';
import { useI18n, useT } from '../../i18n/i18nContext';
import { translate } from '../../i18n/translate';
import {
  ICON_SIZE,
  GoogleIcon,
  XIcon,
  DiscordIcon,
  GitHubIcon,
  AppleIcon,
  FacebookIcon,
} from './LoginIcons';

type Busy = null | 'email' | 'code' | OAuthProvider;

/** Email sign-in steps: the entry button, the address form, then the code form. */
type EmailStep = 'idle' | 'address' | 'code';

/** Supabase's email one-time codes are six digits. */
const EMAIL_CODE_LENGTH = 6;

function loginErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : translate('account.signIn.failed');
}

/**
 * Socials shown as a round-icon row (Google gets its own full-width button above). Only the
 * ones enabled under Supabase → Authentication → Providers are rendered
 * (useEnabledOAuthProviders), so a player never taps a provider that is switched off.
 */
const SOCIAL_PROVIDERS: ReadonlyArray<{ provider: OAuthProvider; label: string; icon: ReactNode }> = [
  { provider: 'x', label: 'X', icon: <XIcon /> },
  { provider: 'discord', label: 'Discord', icon: <DiscordIcon /> },
  { provider: 'github', label: 'GitHub', icon: <GitHubIcon /> },
  { provider: 'apple', label: 'Apple', icon: <AppleIcon /> },
  { provider: 'facebook', label: 'Facebook', icon: <FacebookIcon /> },
];

interface LoginButtonProps {
  /** Class for the trigger button so the header can size it like the connect button. */
  className?: string;
  triggerText?: string;
  /**
   * Optional controlled-open state. When provided, the dialog's open state is driven
   * by the parent (used to open the sign-in flow from another element — e.g. a
   * campaign world card). Pair with `hideTrigger` to drive it without a visible button.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the built-in trigger button (for controlled, externally-opened usage). */
  hideTrigger?: boolean;
  /**
   * Show the primary top "Play Free" button above the sign-in methods.
   * Only for generic entry points like the header Play button —
   * contextual dialogs (adopt, campaign, free-cap) require a real sign-in and must
   * leave this off.
   */
  guestCta?: boolean;
  /** Called when the guest link is clicked (after the dialog closes). Parent handles routing. */
  onGuestPlay?: () => void;
}

/**
 * Inside a game portal's frame there is no Suikamon sign-in window (lib/portalEmbed.ts). On
 * CrazyGames the same button opens Login with CrazyGames, the one login they allow; on any other
 * portal it renders nothing and the visitor plays as a guest.
 */
export function LoginButton(props: LoginButtonProps) {
  const portal = getPortal();
  if (portal === 'crazygames') return <CrazyGamesLoginButton {...props} />;
  if (portal !== null) return null;
  return <SupabaseLoginButton {...props} />;
}

/**
 * Login with CrazyGames behind the LoginButton API, so every existing call site (the header,
 * the guest banner, the free-cap popup, a controlled `open`) works unchanged inside their frame.
 * It never opens by itself: CrazyGames' prompt appears only after a tap. Renders nothing until
 * their SDK reports accounts available.
 */
function CrazyGamesLoginButton({
  className,
  triggerText,
  open,
  onOpenChange,
  hideTrigger = false,
}: LoginButtonProps) {
  const t = useT();
  const account = useContext(AccountContext);
  const available = account?.crazyGamesLoginAvailable ?? false;
  const login = account?.loginWithCrazyGames;
  const [busy, setBusy] = useState(false);

  const run = () => {
    if (!login || busy) return;
    setBusy(true);
    login()
      .catch((error: unknown) => console.error('[crazygames] Login with CrazyGames failed', error))
      .finally(() => {
        setBusy(false);
        onOpenChange?.(false);
      });
  };

  // A controlled `open` comes from a tap elsewhere (a "Sign up" button that owns this dialog).
  useEffect(() => {
    if (open && available) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per open
  }, [open, available]);

  if (!available || hideTrigger) return null;
  return (
    <button type="button" className={className} onClick={run} disabled={busy}>
      {triggerText ?? t('account.signIn.play')}
    </button>
  );
}

function SupabaseLoginButton({
  className,
  triggerText,
  open: openProp,
  onOpenChange,
  hideTrigger = false,
  guestCta = false,
  onGuestPlay,
}: LoginButtonProps) {
  const { t, rich } = useI18n();
  const { sendEmailCode, verifyEmailCode, loginWithOAuth } = useAccountId();

  // Support both uncontrolled (internal state) and controlled (parent-driven) open.
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const [busy, setBusy] = useState<Busy>(null);
  const busyRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [emailStep, setEmailStep] = useState<EmailStep>('idle');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');

  /** Runs one sign-in step; returns whether it succeeded. Ignores clicks while one is running. */
  async function run(method: Busy, fn: () => Promise<void>): Promise<boolean> {
    if (busyRef.current) return false;
    busyRef.current = true;
    setError(null);
    setBusy(method);
    try {
      await fn();
      return true;
    } catch (err) {
      const message = loginErrorMessage(err);
      console.error('[login] Sign-in failed', { method, message });
      setError(message);
      return false;
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  }

  const handleSendCode = async (event: FormEvent) => {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;
    if (await run('email', () => sendEmailCode(address))) {
      setCode('');
      setEmailStep('code');
    }
  };

  const handleVerifyCode = async (event: FormEvent) => {
    event.preventDefault();
    if (await run('code', () => verifyEmailCode(email.trim(), code.trim()))) {
      setEmailStep('idle');
      setOpen(false);
    }
  };

  // OAuth redirects away to the provider; sign-in completes when it redirects back.
  const handleOAuth = (provider: OAuthProvider) => void run(provider, () => loginWithOAuth(provider));
  const enabledProviders = useEnabledOAuthProviders();
  const socialProviders = SOCIAL_PROVIDERS.filter(({ provider }) => enabledProviders.has(provider));

  // Read on each render; a guest pet id means this browser already has a free pet in progress.
  const guestPetId = guestCta ? getGuestFreePetId() : null;

  const handleGuestPlay = () => {
    setOpen(false);
    onGuestPlay?.();
  };

  const disabled = busy !== null;

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <Dialog.Trigger>
          <button type="button" className={className}>
            {triggerText ?? t('account.signIn.play')}
          </button>
        </Dialog.Trigger>
      )}

      <Dialog.Content maxWidth="400px" className="suikamon-login-dialog">
        <div className="suikamon-login-header">
          <img src="/xlogo.png" alt={t('account.signIn.logoAlt')} className="suikamon-login-logo" width={96} height={96} />
          <Dialog.Title className="suikamon-login-title">
            {guestCta ? t('account.signIn.welcomeTitle') : t('account.signIn.title')}
          </Dialog.Title>
          <Dialog.Description size="2" className="suikamon-login-subtitle">
            {guestCta
              ? t('account.signIn.welcomeSubtitle')
              : t('account.signIn.subtitle')}
          </Dialog.Description>
        </div>

        <Flex direction="column" gap="3" className="suikamon-login-methods">
          {guestCta && (
            <>
              <Button
                size="3"
                variant="soft"
                disabled={disabled}
                onClick={handleGuestPlay}
                className="suikamon-login-method suikamon-login-guest-primary suikamon-login-method--social"
              >
                <span className="suikamon-login-method-icon">
                  <Gamepad2 size={ICON_SIZE} aria-hidden="true" />
                </span>
                <span className="suikamon-login-method-label">
                  {guestPetId ? t('account.signIn.continueGuestPet') : t('account.signIn.playAsGuest')}
                </span>
              </Button>
              <div className="suikamon-login-divider">
                <span>{t('account.signIn.orSignIn')}</span>
              </div>
            </>
          )}

          {emailStep === 'idle' && (
            <Button
              size="3"
              variant="soft"
              disabled={disabled}
              onClick={() => {
                setError(null);
                setEmailStep('address');
              }}
              className="suikamon-login-method suikamon-login-method--social"
            >
              <span className="suikamon-login-method-icon">
                <Mail size={ICON_SIZE} aria-hidden="true" />
              </span>
              <span className="suikamon-login-method-label">{t('account.signIn.withEmail')}</span>
            </Button>
          )}

          {emailStep === 'address' && (
            <form className="suikamon-login-email-form" onSubmit={handleSendCode}>
              <TextField.Root
                size="3"
                type="email"
                autoComplete="email"
                required
                autoFocus
                placeholder={t('account.signIn.emailPlaceholder')}
                aria-label={t('account.signIn.emailAria')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={disabled}
                className="suikamon-login-input"
              />
              <Button size="3" type="submit" disabled={disabled || !email.trim()} className="suikamon-login-submit">
                {busy === 'email' ? t('account.signIn.sending') : t('account.signIn.emailMeCode')}
              </Button>
            </form>
          )}

          {emailStep === 'code' && (
            <form className="suikamon-login-email-form" onSubmit={handleVerifyCode}>
              <Text size="2" className="suikamon-login-email-hint">
                {rich('account.signIn.codeSent', { email: email.trim(), b: (chunks) => <strong>{chunks}</strong> })}
              </Text>
              <TextField.Root
                size="3"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                autoFocus
                maxLength={EMAIL_CODE_LENGTH}
                placeholder="123456"
                aria-label={t('account.signIn.codeAria')}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                disabled={disabled}
                className="suikamon-login-input"
              />
              <Button
                size="3"
                type="submit"
                disabled={disabled || code.length !== EMAIL_CODE_LENGTH}
                className="suikamon-login-submit"
              >
                {busy === 'code' ? t('account.signIn.signingIn') : t('account.signIn.signIn')}
              </Button>
              <button
                type="button"
                className="suikamon-login-text-link"
                disabled={disabled}
                onClick={() => {
                  setError(null);
                  setEmailStep('address');
                }}
              >
                {t('account.signIn.differentEmail')}
              </button>
            </form>
          )}

          {/* Only providers switched on in Supabase are shown — see useEnabledOAuthProviders. */}
          {enabledProviders.has('google') && (
            <Button
              size="3"
              variant="soft"
              disabled={disabled}
              onClick={() => handleOAuth('google')}
              className="suikamon-login-method suikamon-login-method--social"
            >
              <span className="suikamon-login-method-icon">
                <GoogleIcon />
              </span>
              <span className="suikamon-login-method-label">{t('account.signIn.withGoogle')}</span>
            </Button>
          )}

          {socialProviders.length > 0 && (
            <div className="suikamon-login-social-row">
              {socialProviders.map(({ provider, label, icon }) => (
                <button
                  key={provider}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleOAuth(provider)}
                  className="suikamon-login-social-icon"
                  aria-label={t('account.signIn.withProvider', { provider: label })}
                  title={t('account.signIn.withProvider', { provider: label })}
                >
                  {icon}
                </button>
              ))}
            </div>
          )}
        </Flex>

        {error && (
          <Text size="1" color="red" mt="2" role="alert" className="suikamon-login-error">
            {error}
          </Text>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
